import React, { useState, useEffect } from "react";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import MainCalculatorsList from "./MainCalculators";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Snackbar,
  Alert,
  Button
} from '@mui/material';

const TITLE = "Compteur électronique - Compteur de pointages en ligne";
const DESCRIPTION = "Utilisez le compteur électronique en ligne pour compter facilement vos répétitions, pointages ou tout autre événement. Le compteur sauvegarde automatiquement votre progression même après la fermeture de la page.";
const KEYWORDS = "compteur electronique, compteur de pointages, tally counter, compteur en ligne, compteur repetitions";
const OG_URL = "https://do-calculate.com/calculator/fr/tally-counter/";

const TallyCounter = () => {
  const [state, setState] = useState(() => {
    const savedCounts = JSON.parse(localStorage.getItem("fr_counts")) || {};
    const savedCustom = JSON.parse(localStorage.getItem("fr_customCounters")) || [];
    const savedGoals = JSON.parse(localStorage.getItem("fr_counterGoals")) || {};
    return {
      counts: {
        counter1: savedCounts.counter1 || 0,
        counter2: savedCounts.counter2 || 0,
        counter3: savedCounts.counter3 || 0,
        counter4: savedCounts.counter4 || 0,
        counter5: savedCounts.counter5 || 0,
        ...savedCounts
      },
      customCounters: savedCustom,
      goals: savedGoals
    };
  });

  const [showModal, setShowModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [currentGoalCounter, setCurrentGoalCounter] = useState("");
  const [goalValue, setGoalValue] = useState("");
  const [newCounter, setNewCounter] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const playGoalSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Audio play failed:', error);
    }
  };

  useEffect(() => {
    localStorage.setItem("fr_counts", JSON.stringify(state.counts));
    localStorage.setItem("fr_customCounters", JSON.stringify(state.customCounters));
    localStorage.setItem("fr_counterGoals", JSON.stringify(state.goals));
  }, [state]);

  const handleIncrement = (key) => {
    setState(prev => {
      const newCount = (prev.counts[key] || 0) + 1;
      const newState = {
        ...prev,
        counts: { ...prev.counts, [key]: newCount }
      };
      if (prev.goals[key] && newCount === prev.goals[key]) {
        playGoalSound();
        setSnackbarMessage(`Félicitations ! Vous avez atteint votre objectif de ${prev.goals[key]} !`);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
      return newState;
    });
  };

  const handleDecrement = (key) => {
    setState(prev => ({
      ...prev,
      counts: { ...prev.counts, [key]: Math.max((prev.counts[key] || 0) - 1, 0) }
    }));
  };

  const handleReset = (key) => {
    setState(prev => ({
      ...prev,
      counts: { ...prev.counts, [key]: 0 }
    }));
  };

  const handleAddCounter = () => {
    if (newCounter.trim() === "") return;
    const key = `custom_${Date.now()}`;
    setState(prev => ({
      ...prev,
      customCounters: [...prev.customCounters, { title: newCounter, key }],
      counts: { ...prev.counts, [key]: 0 }
    }));
    setNewCounter("");
    setShowModal(false);
  };

  const handleRemoveCounter = (key) => {
    setState(prev => {
      const newCounts = { ...prev.counts };
      const newGoals = { ...prev.goals };
      delete newCounts[key];
      delete newGoals[key];
      return {
        ...prev,
        customCounters: prev.customCounters.filter(item => item.key !== key),
        counts: newCounts,
        goals: newGoals
      };
    });
  };

  const handleRemoveGoal = (key) => {
    setState(prev => {
      const newGoals = { ...prev.goals };
      delete newGoals[key];
      return { ...prev, goals: newGoals };
    });
  };

  const handleSetGoal = (key) => {
    setCurrentGoalCounter(key);
    setGoalValue(state.goals[key] || "");
    setShowGoalModal(true);
  };

  const handleSaveGoal = () => {
    if (!goalValue || isNaN(goalValue)) return;
    setState(prev => ({
      ...prev,
      goals: { ...prev.goals, [currentGoalCounter]: parseInt(goalValue) }
    }));
    setShowGoalModal(false);
    setGoalValue("");
    setCurrentGoalCounter("");
  };

  const defaultCounters = [
    { title: "Subhanallah (Gloire à Allah)", key: "counter1" },
    { title: "Alhamdulillah (Louange à Allah)", key: "counter2" },
    { title: "Allahu Akbar (Allah est le plus grand)", key: "counter3" },
    { title: "Astaghfirullah (Je demande pardon à Allah)", key: "counter4" },
    { title: "La Ilaha Illallah (Il n'y a de dieu qu'Allah)", key: "counter5" },
  ];

  const allCounters = [...defaultCounters, ...state.customCounters];

  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <div className="bg-success text-white text-center" style={{ height: "50px" }}>
            <h1 style={{ fontSize: "17px" }}>Compteur électronique - Compteur de pointages</h1>
          </div>
          <div className="border border-light shadow-lg p-4 rounded" style={{
            backgroundColor: "#f8f9fa",
            borderRadius: "10px",
            boxShadow: "0px 4px 15px rgba(0,0,0,0.1)",
          }}>
            <div className="row">
              <div>
                <p>
                  Le compteur électronique est un outil numérique qui vous aide à comptabiliser des répétitions, des événements ou tout autre élément à dénombrer.
                  Appuyez sur le cercle pour incrémenter le compteur, utilisez les boutons pour décrémenter ou réinitialiser.{" "}
                  <b>
                    Votre compteur est sauvegardé automatiquement, même si vous actualisez ou fermez la page, afin de reprendre là où vous vous étiez arrêté.
                  </b>{" "}
                  Vous pouvez également définir des objectifs pour chaque compteur et recevoir une alerte sonore lorsque l'objectif est atteint.
                </p>
                <b>Exemples d&apos;utilisation :</b>
                <ul>
                  {defaultCounters.map(item => (
                    <li key={item.key}>{item.title}</li>
                  ))}
                </ul>
                <p>
                  Vous pouvez également ajouter vos propres compteurs personnalisés via le bouton ci-dessous.
                </p>
                <div className="my-3">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(true)}
                  >
                    Ajouter un compteur
                  </button>
                </div>
              </div>
            </div>
            <br />
            {allCounters.map((item) => (
              <div className="row" key={item.key} style={{ paddingBottom: "15px" }}>
                <div className="col calculatorStyle">
                  <div className="p-3 mb-2 bg-success text-white text-center">
                    <h2 style={{ fontSize: "24px" }}>{item.title}</h2>
                    {state.customCounters.some(c => c.key === item.key) && (
                      <button
                        className="text-white px-4 py-2 btn btn-secondary"
                        onClick={() => handleRemoveCounter(item.key)}
                        style={{ float: "right", marginTop: "-35px" }}
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                  <hr />
                  <div>
                    <div className="text-center my-4">
                      <div
                        style={{
                          background: "linear-gradient(135deg, #ffffff 0%, #e0f7fa 100%)",
                          color: "#4caf50",
                          borderRadius: "50%",
                          width: "300px",
                          height: "300px",
                          lineHeight: "300px",
                          fontSize: "70px",
                          fontWeight: "bold",
                          margin: "0 auto",
                          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
                          cursor: "pointer",
                          userSelect: "none",
                          alignItems: "center",
                          display: "flex",
                          justifyContent: "center",
                        }}
                        onClick={() => handleIncrement(item.key)}
                      >
                        {state.counts[item.key] || 0}
                      </div>
                    </div>
                    <br />
                    <div className="text-center mt-2">
                      <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                        <button
                          className="text-white px-4 py-2"
                          style={{
                            background: "linear-gradient(135deg, #f44336 0%, #e57373 100%)",
                            borderRadius: "20px",
                            fontSize: "16px",
                            fontWeight: "bold",
                            border: "none",
                            boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
                            padding: "15px",
                          }}
                          onClick={() => handleDecrement(item.key)}
                        >
                          Décrémenter
                        </button>
                        <button
                          className="text-white px-4 py-2"
                          style={{
                            background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                            borderRadius: "20px",
                            fontSize: "16px",
                            fontWeight: "bold",
                            border: "none",
                            boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
                            padding: "15px",
                          }}
                          onClick={() => handleReset(item.key)}
                        >
                          Réinitialiser
                        </button>
                        <button
                          className="text-white px-4 py-2"
                          style={{
                            background: "linear-gradient(135deg, #4caf50 0%, #81c784 100%)",
                            borderRadius: "20px",
                            fontSize: "16px",
                            fontWeight: "bold",
                            border: "none",
                            boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)",
                            padding: "15px",
                          }}
                          onClick={() => handleSetGoal(item.key)}
                        >
                          Définir un objectif
                        </button>
                      </div>
                      {state.goals[item.key] && (
                        <div className="mt-2" style={{ fontSize: "16px", color: "#4caf50" }}>
                          <hr />
                          Objectif : {state.goals[item.key]} — Restant : {Math.max(state.goals[item.key] - (state.counts[item.key] || 0), 0)}
                          <br />
                          <button
                            className="text-white px-2 py-1 ms-2"
                            style={{
                              background: "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)",
                              borderRadius: "15px",
                              fontSize: "18px",
                              fontWeight: "bold",
                              border: "none",
                              boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
                              marginTop: "10px",
                            }}
                            onClick={() => handleRemoveGoal(item.key)}
                          >
                            Supprimer l&apos;objectif
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr style={{ width: "100%" }} />
            </div>
          </div>
        </div>
        <div className="col">
          <MainCalculatorsList />
          <br />
        </div>
      </div>

      <Dialog open={showModal} onClose={() => setShowModal(false)}>
        <DialogTitle>Ajouter un nouveau compteur</DialogTitle>
        <DialogContent>
          <Box sx={{ minWidth: 300, padding: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              id="counter-text"
              label="Nom du compteur"
              type="text"
              fullWidth
              variant="outlined"
              value={newCounter}
              onChange={(e) => setNewCounter(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)} color="secondary">
            Annuler
          </Button>
          <Button onClick={handleAddCounter} color="primary" variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showGoalModal} onClose={() => setShowGoalModal(false)}>
        <DialogTitle>Définir un objectif</DialogTitle>
        <DialogContent>
          <Box sx={{ minWidth: 300, padding: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              id="goal-value"
              label="Nombre cible"
              type="number"
              fullWidth
              variant="outlined"
              value={goalValue}
              onChange={(e) => setGoalValue(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {state.goals[currentGoalCounter] && (
            <Button
              onClick={() => {
                handleRemoveGoal(currentGoalCounter);
                setShowGoalModal(false);
              }}
              color="error"
            >
              Supprimer l&apos;objectif
            </Button>
          )}
          <Button onClick={() => setShowGoalModal(false)} color="secondary">
            Annuler
          </Button>
          <Button onClick={handleSaveGoal} color="primary" variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TallyCounter;